import { useMemo, type PropsWithChildren } from "react";

import { type AirHandlerUnit, type BoilerRoom, type Floor } from "@eisox/api/core-api";
import { createContext } from "@radix-ui/react-context";

import type { EnhancedGateway } from "~/types/gateway.type";
import type { EnhancedMultisensor } from "~/types/multisensor.type";
import type { EnhancedRoom } from "~/types/room.type";
import type { EnhancedValve } from "~/types/valve.type";

import { useBuildingContext } from "./building.provider";

interface FloorProviderProps extends PropsWithChildren {
  floorId: string;
}

interface FloorContextValue {
  floor: Floor;
  rooms: EnhancedRoom[];
  gateways: EnhancedGateway[];
  valves: EnhancedValve[];
  multisensors: EnhancedMultisensor[];
  boilerRooms: (BoilerRoom & { floor: { id: string; x: number; y: number } })[];
  airHandlerUnits: (AirHandlerUnit & { floor: { id: string; x: number; y: number } })[];
}

const [FloorContextProvider, useFloorContext] = createContext<FloorContextValue>("FloorProvider");

const FloorProvider: React.FC<FloorProviderProps> = ({ floorId, children }) => {
  const { floors, rooms, gateways, valves, multisensors, boilerRooms, airHandlerUnits } =
    useBuildingContext("FloorProvider");

  const floor = useMemo(() => floors.find(f => f.id === floorId), [floors, floorId]);
  if (!floor) {
    throw new Error(`Floor with id ${floorId} not found`);
  }

  const enhancedRooms = useMemo(() => rooms.filter(r => r.floor.id === floorId), [rooms, floorId]);
  const enhancedGateways = useMemo(() => gateways.filter(g => g.floor.id === floorId), [gateways, floorId]);
  const enhancedValves = useMemo(() => valves.filter(v => v.floor.id === floorId), [valves, floorId]);
  const enhancedMultisensors = useMemo(() => multisensors.filter(m => m.floor.id === floorId), [multisensors, floorId]);
  const enhancedBoilerRooms = useMemo(
    () => boilerRooms.filter(withFloorDefined).filter(br => br.floor.id === floorId),
    [boilerRooms, floorId],
  );
  const enhancedAirHandlerUnits = useMemo(
    () => airHandlerUnits.filter(withFloorDefined).filter(ahu => ahu.floor.id === floorId),
    [airHandlerUnits, floorId],
  );

  return (
    <FloorContextProvider
      floor={floor}
      rooms={enhancedRooms}
      gateways={enhancedGateways}
      valves={enhancedValves}
      multisensors={enhancedMultisensors}
      boilerRooms={enhancedBoilerRooms}
      airHandlerUnits={enhancedAirHandlerUnits}
    >
      {children}
    </FloorContextProvider>
  );
};

const withFloorDefined = <T extends { floor?: { id: string; x: number; y: number } }>(
  item: T,
): item is T & { floor: { id: string; x: number; y: number } } => {
  return item.floor !== undefined;
};

export { FloorProvider, useFloorContext };
